/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { FinancialGateway, CustomerData, CustomerResponse, TransferRequest, TransferResponse, WebhookProcessResult } from "../FinancialGateway";
import { PaymentGateway } from "../PaymentGateway";
import { PaymentRequest, PaymentResponse, PaymentStatusResponse } from "../types/financial.types";
import {
  CreateSubscriptionRequest,
  SubscriptionResponse,
  CancelSubscriptionResponse,
  SubscriptionRecord,
} from "../types/subscription.types";
import { getSystemSettings } from "../../../utils/commissionConfig";
import { generateFallbackPixPayload } from "../utils/financialCalculations";

/**
 * Placeholder / Stub Arquitetural para o Gateway Mercado Pago.
 *
 * NOTA DE AUDITORIA:
 * O Asaas é o único gateway real homologado no momento.
 * Este stub mantém a conformidade com as interfaces FinancialGateway e PaymentGateway,
 * permitindo plugar a integração completa do Mercado Pago futuramente sem quebrar contratos
 * ou disparar chamadas externas desprotegidas no frontend.
 */
export class MercadoPagoGateway implements FinancialGateway, PaymentGateway {
  readonly id = "mercadopago" as const;
  readonly name = "Mercado Pago (Placeholder Arquitetural)";

  async createCustomer(data: CustomerData): Promise<CustomerResponse> {
    return {
      success: true,
      customerId: `mp_cust_stub_${Date.now()}`,
      customer: data,
    };
  }

  async getCustomer(customerId: string): Promise<CustomerResponse> {
    return {
      success: true,
      customerId,
      customer: { id: customerId, name: "Mercado Pago Customer Placeholder" },
    };
  }

  async createSubscription(request: CreateSubscriptionRequest): Promise<SubscriptionResponse> {
    const subId = `mp_sub_stub_${Date.now()}`;
    return {
      success: true,
      subscriptionId: subId,
      gatewaySubscriptionId: subId,
      status: "ACTIVE",
      cycle: request.cycle,
      value: request.value,
      nextDueDate: request.nextDueDate || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split("T")[0],
      gateway: "mercadopago",
      createdAt: new Date().toISOString(),
    };
  }

  async getSubscription(subscriptionId: string): Promise<{ success: boolean; subscription?: SubscriptionRecord; raw?: any; errorMessage?: string }> {
    const record: SubscriptionRecord = {
      id: subscriptionId,
      userId: "",
      gateway: "mercadopago",
      gatewaySubscriptionId: subscriptionId,
      planId: "mp_plan_stub",
      value: 0,
      cycle: "MONTHLY",
      status: "ACTIVE",
      startDate: new Date().toISOString(),
      nextDueDate: new Date().toISOString().split("T")[0],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    return { success: true, subscription: record };
  }

  async cancelSubscription(subscriptionId: string, reason?: string): Promise<CancelSubscriptionResponse> {
    return {
      success: true,
      subscriptionId,
      gatewaySubscriptionId: subscriptionId,
      status: "CANCELLED",
      cancellationDate: new Date().toISOString(),
      message: "Assinatura cancelada no Mercado Pago (Stub)",
    };
  }

  getAccessToken(customToken?: string): string {
    if (customToken && customToken.trim().length > 10) {
      return customToken.trim();
    }
    const settings = getSystemSettings();
    if (settings.mercadoPagoKey && settings.mercadoPagoKey.trim().length > 10) {
      return settings.mercadoPagoKey.trim();
    }
    return "";
  }

  async createPayment(params: PaymentRequest): Promise<PaymentResponse> {
    const rawAmount = params.amount !== undefined && params.amount !== null ? Number(params.amount) : NaN;
    if (isNaN(rawAmount) || rawAmount <= 0) {
      throw new Error("O valor da cobrança (amount) é obrigatório, deve ser um número válido e maior que zero.");
    }
    const safeAmount = Number(rawAmount.toFixed(2));

    const fallback = generateFallbackPixPayload(
      params.arenaPixKey || getSystemSettings().masterPixKey,
      safeAmount,
      "ARENA ESPORTIVA"
    );

    return {
      success: true,
      paymentId: `PIX_MP_STUB_${Date.now()}`,
      qrCode: fallback.qrCode,
      qrCodeBase64: fallback.qrCodeBase64,
      status: "pending",
      gateway: "pix_direto",
      isRealGateway: false,
      errorMessage: "Mercado Pago em modo arquitetural. O gateway oficial de pagamentos e repasses homologado é o Asaas.",
    };
  }

  async checkPaymentStatus(paymentId: string): Promise<PaymentStatusResponse> {
    return {
      status: "pending",
      isPaid: false,
    };
  }

  async createTransfer?(request: TransferRequest): Promise<TransferResponse> {
    return {
      success: false,
      status: "FAILED",
      errorMessage: "Transferências bancárias e repasses são homologados exclusivamente via Asaas.",
    };
  }

  async processWebhook?(payload: any): Promise<WebhookProcessResult> {
    return {
      success: true,
      handled: true,
      eventType: payload?.type || payload?.action || "MP_WEBHOOK_STUB",
      status: "IGNORED",
      message: "Webhook Mercado Pago recebido pelo stub arquitetural.",
    };
  }
}

